import { useState, useCallback } from 'react';

interface User {
  id: number;
  name: string;
  email: string;
  createdAt?: string;
}

interface UserData {
  name: string;
  email: string;
}

interface UseUsersReturn {
  users: User[];
  loading: boolean;
  error: string | null;
  fetchUsers: () => Promise<void>;
  createUser: (userData: UserData) => Promise<User>;
}

export const useUsers = (): UseUsersReturn => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/users');

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status}`);
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createUser = useCallback(
    async (userData: UserData): Promise<User> => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch('/api/users', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userData),
        });

        if (!response.ok) {
          throw new Error(`Failed to create user: ${response.status}`);
        }

        const data = await response.json();

        // Refresh users list after creating
        await fetchUsers();

        return data.user;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Error creating user:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchUsers],
  );

  return {
    users,
    loading,
    error,
    fetchUsers,
    createUser,
  };
};
